import type { CaseStatus, Severity } from './types'

const PACIFIC = 'America/Los_Angeles'

export function formatDateTime(iso?: string | null, options: Intl.DateTimeFormatOptions = {}) {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: PACIFIC,
    ...options,
  })
}

export function formatLongDateTime(iso?: string | null) {
  return formatDateTime(iso, {
    weekday: 'long',
    month: 'long',
    timeZoneName: 'short',
  })
}

export function formatDateOnly(iso?: string | null) {
  return formatDateTime(iso, { hour: undefined, minute: undefined })
}

export function relativeTime(iso?: string | null) {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return '—'
  const seconds = Math.round((Date.now() - then) / 1000)
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31536000],
    ['month', 2592000],
    ['week', 604800],
    ['day', 86400],
    ['hour', 3600],
    ['minute', 60],
  ]
  const formatter = new Intl.RelativeTimeFormat('en-US', { numeric: 'auto' })
  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size) return formatter.format(-Math.round(seconds / size), unit)
  }
  return formatter.format(-seconds, 'second')
}

export function clock(seconds?: number | null) {
  if (!Number.isFinite(seconds ?? NaN)) return '0:00'
  const total = Math.max(0, Math.floor(seconds as number))
  const minutes = Math.floor(total / 60)
  const secs = total % 60
  return `${minutes}:${String(secs).padStart(2, '0')}`
}

export function preciseClock(seconds?: number | null) {
  if (!Number.isFinite(seconds ?? NaN)) return '0:00.00'
  const value = Math.max(0, seconds as number)
  const minutes = Math.floor(value / 60)
  const secs = value - minutes * 60
  return `${minutes}:${secs.toFixed(2).padStart(5, '0')}`
}

export function fileSize(bytes?: number) {
  if (!bytes) return '—'
  const units = ['B', 'KB', 'MB', 'GB']
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${value < 10 && unit > 0 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`
}

/** Converts an ISO timestamp into the value a datetime-local input expects. */
export function toLocalInput(iso?: string | null) {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`
}

export function fromLocalInput(value: string) {
  if (!value) return null
  const match = value.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,6}))?)?/,
  )
  if (match) {
    const ms = match[7] ? Number(match[7].padEnd(3, '0').slice(0, 3)) : 0
    const date = new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
      Number(match[4]),
      Number(match[5]),
      Number(match[6] || 0),
      ms,
    )
    return Number.isNaN(date.getTime()) ? null : date.toISOString()
  }
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

export const SEVERITY_STYLE: Record<Severity, { label: string; className: string; dot: string }> = {
  low: {
    label: 'Low',
    className: 'border-sky-400/25 bg-sky-400/10 text-sky-300',
    dot: 'bg-sky-400',
  },
  moderate: {
    label: 'Moderate',
    className: 'border-amber-400/25 bg-amber-400/10 text-amber-300',
    dot: 'bg-amber-400',
  },
  severe: {
    label: 'Severe',
    className: 'border-red-500/30 bg-red-500/12 text-red-300',
    dot: 'bg-red-500',
  },
}

export const STATUS_STYLE: Record<CaseStatus, { label: string; className: string; dot: string }> = {
  draft: {
    label: 'Draft',
    className: 'border-white/12 bg-white/[0.05] text-slate-400',
    dot: 'bg-slate-500',
  },
  submitted: {
    label: 'Submitted',
    className: 'border-altair-500/30 bg-altair-500/12 text-altair-300',
    dot: 'bg-altair-500',
  },
  under_review: {
    label: 'Under review',
    className: 'border-violet-400/25 bg-violet-400/10 text-violet-300',
    dot: 'bg-violet-400',
  },
  notice_sent: {
    label: 'Notice sent',
    className: 'border-cyan-400/25 bg-cyan-400/10 text-cyan-300',
    dot: 'bg-cyan-400',
  },
  fine_issued: {
    label: 'Fine issued',
    className: 'border-amber-400/25 bg-amber-400/10 text-amber-300',
    dot: 'bg-amber-400',
  },
  resolved: {
    label: 'Resolved',
    className: 'border-emerald-400/25 bg-emerald-400/10 text-emerald-300',
    dot: 'bg-emerald-400',
  },
  dismissed: {
    label: 'Dismissed',
    className: 'border-white/10 bg-white/[0.04] text-slate-500',
    dot: 'bg-slate-600',
  },
}

export function plateDisplay(plate?: string) {
  const value = (plate || '').trim().toUpperCase()
  return value || 'NO PLATE'
}

export function cx(...values: (string | false | null | undefined)[]) {
  return values.filter(Boolean).join(' ')
}
